import { ChangeDetectionStrategy, Component, computed, inject, signal, type OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, type PlatformKey } from '@/cloud/services/cloud-api.service';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { RouterLink } from '@angular/router';

const DAY = 24 * 60 * 60 * 1000;

/**
 * Superadmin management of MCP platform keys: operator credentials that let an AI agent read
 * across tenants (an allowlist or all of them) without each tenant's opt-in. They always expire
 * (at most a year), are shown once, and every call is audited against the tenants it touched.
 */
@Component({
    selector: 'app-platform-keys',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, ConfirmDialogModule, DatePickerModule, DialogModule, InputTextModule, MessageModule, MultiSelectModule, RadioButtonModule, TableModule, TagModule, BreadcrumbsComponent, EmptyStateComponent, RouterLink],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './platform-keys.scss',
    template: `
        <p-confirmdialog />
        <div class="management-page">
            <app-breadcrumbs [model]="breadcrumbs" />
            <header class="overview-header">
                <div>
                    <p class="overview-eyebrow">Administration</p>
                    <h1>Platform keys</h1>
                    <p class="overview-description">Operator credentials for AI agents that read across tenants through the MCP endpoint.</p>
                </div>
                <div class="overview-actions">
                    <p-button label="New key" icon="pi pi-plus" (onClick)="openCreate()" />
                </div>
            </header>

            <p-message severity="warn" class="platform-keys-warning">
                Platform keys ignore each tenant's AI agent opt-in. Every call is recorded in the audit log of each tenant it reads, where tenant admins can see it. Issue them sparingly and keep expiry short.
            </p-message>

            <div class="platform-assistant-bar">
                <i class="pi pi-comments" aria-hidden="true"></i>
                <span><strong>Service desk assistant</strong> — who can ask the assistant across tenants, and the Anthropic key it runs on.</span>
                <p-button label="Manage" icon="pi pi-arrow-right" iconPos="right" size="small" [outlined]="true" routerLink="/admin/service-desk" />
            </div>

            <section class="management-panel" aria-label="Platform keys">
                <p-table class="management-table" [value]="keys()" [loading]="loading()" [scrollable]="true">
                    <ng-template #header>
                        <tr>
                            <th>Name</th>
                            <th>Tenants</th>
                            <th class="hidden md:table-cell">Expires</th>
                            <th class="hidden md:table-cell">Last used</th>
                            <th>Status</th>
                            <th><span class="sr-only">Actions</span></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-key>
                        <tr>
                            <td class="font-medium">
                                <div class="min-w-40">{{ key.name }}</div>
                            </td>
                            <td class="text-sm">
                                <div class="min-w-48">{{ key.allNamespaces ? 'All tenants' : tenantNames(key) }}</div>
                            </td>
                            <td class="hidden md:table-cell text-sm">{{ key.expiresUtc | date: 'mediumDate' }}</td>
                            <td class="hidden md:table-cell text-sm">{{ key.lastUsedUtc ? (key.lastUsedUtc | date: 'short') : 'Never' }}</td>
                            <td><p-tag [value]="status(key)" [severity]="status(key) === 'Active' ? 'success' : 'secondary'" [rounded]="true" /></td>
                            <td>
                                <div class="management-row-actions">
                                    @if (status(key) === 'Active') {
                                        <p-button label="Revoke" severity="danger" [text]="true" size="small" [ariaLabel]="'Revoke ' + key.name" (onClick)="revoke(key)" />
                                    }
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="6">
                                <app-empty-state icon="pi pi-key" title="No platform keys" />
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </section>
        </div>

        <p-dialog header="New platform key" [(visible)]="showCreate" [modal]="true" [style]="{ width: '34rem' }" [draggable]="false">
            @if (created(); as c) {
                <p-message severity="success" class="mb-3 w-full">Copy the key now — it is not shown again.</p-message>
                <div class="flex items-center gap-2">
                    <code class="p-2 rounded-border flex-1" style="background: var(--p-content-hover-background); word-break: break-all">{{ c.key }}</code>
                    <button pButton iconOnly type="button" class="p-button-text" (click)="copy(c.key!)"><i class="pi pi-copy p-button-icon" aria-hidden="true"></i></button>
                </div>
                <div class="flex justify-end mt-4">
                    <button pButton type="button" (click)="closeCreate()"><span class="p-button-label">Done</span></button>
                </div>
            } @else {
                <label class="block mb-2 font-medium" for="pk-name">Name</label>
                <input pInputText id="pk-name" class="w-full mb-4" [(ngModel)]="name" placeholder="e.g. Support triage agent" />

                <span class="block mb-2 font-medium">Tenants</span>
                <div class="flex flex-col gap-2 mb-3">
                    <div class="flex items-center gap-2">
                        <p-radiobutton inputId="pk-some" name="scope" value="some" [(ngModel)]="scope" />
                        <label for="pk-some">Selected tenants</label>
                    </div>
                    <div class="flex items-center gap-2">
                        <p-radiobutton inputId="pk-all" name="scope" value="all" [(ngModel)]="scope" />
                        <label for="pk-all">All tenants, including ones created later</label>
                    </div>
                </div>
                @if (scope() === 'some') {
                    <p-multiselect [options]="tenantOptions()" [(ngModel)]="selectedTenants" optionLabel="name" optionValue="id" placeholder="Choose tenants" [filter]="true" display="chip" class="w-full mb-4" appendTo="body" />
                }

                <label class="block mb-2 font-medium">Expires</label>
                <p-datepicker [(ngModel)]="expires" [minDate]="minExpiry" [maxDate]="maxExpiry" dateFormat="dd/mm/yy" class="w-full mb-4" appendTo="body" />

                <div class="flex justify-end gap-2">
                    <button pButton type="button" class="p-button-text" (click)="closeCreate()"><span class="p-button-label">Cancel</span></button>
                    <button pButton type="button" (click)="create()" [disabled]="!canCreate() || saving()"><span class="p-button-label">Create key</span></button>
                </div>
            }
        </p-dialog>
    `
})
export class PlatformKeys implements OnInit {
    private api = inject(CloudApiService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);

    readonly breadcrumbs: BreadcrumbItem[] = [
        { label: 'Dashboard', routerLink: '/dashboard' },
        { label: 'Platform keys', routerLink: '/admin/platform-keys' }
    ];
    keys = signal<PlatformKey[]>([]);
    loading = signal(false);
    saving = signal(false);
    showCreate = false;
    created = signal<PlatformKey | null>(null);

    name = signal('');
    scope = signal<'some' | 'all'>('some');
    selectedTenants = signal<string[]>([]);
    expires = signal<Date>(new Date(Date.now() + 90 * DAY));
    readonly minExpiry = new Date(Date.now() + DAY);
    readonly maxExpiry = new Date(Date.now() + 365 * DAY);

    tenantOptions = computed(() => (this.api.namespaces() ?? []).map((n) => ({ id: n.id ?? n.Id, name: n.name ?? n.Name ?? n.id })).sort((a, b) => String(a.name).localeCompare(String(b.name))));
    canCreate = computed(() => this.name().trim().length > 0 && (this.scope() === 'all' || this.selectedTenants().length > 0) && !!this.expires());

    ngOnInit(): void {
        this.api.getNamespaces().catch(() => undefined);
        this.refresh();
    }

    refresh() {
        this.loading.set(true);
        this.api
            .getPlatformKeys()
            .then((keys) => this.keys.set(keys))
            .catch((err) => this.toastError('Failed to load platform keys', err))
            .finally(() => this.loading.set(false));
    }

    status(key: PlatformKey): 'Active' | 'Revoked' | 'Expired' {
        if (key.revoked) return 'Revoked';
        return new Date(key.expiresUtc).getTime() <= Date.now() ? 'Expired' : 'Active';
    }

    tenantNames(key: PlatformKey): string {
        return key.namespaces.map((n) => n.name).join(', ');
    }

    openCreate() {
        this.created.set(null);
        this.name.set('');
        this.scope.set('some');
        this.selectedTenants.set([]);
        this.expires.set(new Date(Date.now() + 90 * DAY));
        this.showCreate = true;
    }

    closeCreate() {
        this.showCreate = false;
        this.created.set(null);
    }

    create() {
        this.saving.set(true);
        this.api
            .createPlatformKey({
                name: this.name().trim(),
                allNamespaces: this.scope() === 'all',
                namespaceIds: this.scope() === 'all' ? [] : this.selectedTenants(),
                expiresUtc: this.expires().toISOString()
            })
            .then((key) => {
                this.created.set(key);
                this.refresh();
            })
            .catch((err) => this.toastError('Failed to create the key', err))
            .finally(() => this.saving.set(false));
    }

    revoke(key: PlatformKey) {
        this.confirmationService.confirm({
            header: 'Revoke platform key',
            message: `Revoke "${key.name}"? Agents using it lose access within a minute. This cannot be undone.`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Revoke',
            accept: () => {
                this.api
                    .revokePlatformKey(key.id)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Key revoked', detail: key.name });
                        this.refresh();
                    })
                    .catch((err) => this.toastError('Failed to revoke the key', err));
            }
        });
    }

    copy(text: string) {
        navigator.clipboard?.writeText(text);
        this.messageService.add({ severity: 'info', summary: 'Copied to clipboard' });
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
